import React from 'react';
import {
  Box,
  Typography,
  Divider,
  Card,
  CardContent,
  Avatar,
  Stack,
  Chip,
  useTheme,
  useMediaQuery,
  Alert,
} from '@mui/material';
import { useCart } from '../CartContext';
import { useCurrency } from '../CurrencyContext';
import {
  getPriceInCurrency,
  getCurrencyDecimals,
  convertDeliveryFee,
} from '../currencyHelpers';
import config from '../config';
import { useTranslation } from 'react-i18next';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// ✅ NEW: minimum order amount in YER (matches mobile)
const MIN_ORDER_YER = 1000;

const getBranchId = (b) => b?.Id ?? b?.id ?? null;
const getBranchName = (b) => b?.Name || b?.name || 'Branch';
const getBranchAddress = (b) => b?.Address || b?.address || '';

const getImageUrls = (imageData) => {
  const fallbackUrl =
    'https://kaybees.com.pk/cdn/shop/products/ChickenBurger_1_1512x.jpg?v=1686040732';
  if (!imageData)
    return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
  if (typeof imageData === 'object' && !Array.isArray(imageData)) {
    if (imageData.thumbnail && imageData.medium && imageData.large) {
      return {
        thumbnail: imageData.thumbnail.startsWith('http')
          ? imageData.thumbnail
          : `${config.SITE_BASE_URL}${imageData.thumbnail}`,
        medium: imageData.medium.startsWith('http')
          ? imageData.medium
          : `${config.SITE_BASE_URL}${imageData.medium}`,
        large: imageData.large.startsWith('http')
          ? imageData.large
          : `${config.SITE_BASE_URL}${imageData.large}`,
      };
    }
    return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
  }
  if (typeof imageData === 'string') {
    let fullUrl;
    if (imageData.startsWith('http')) fullUrl = imageData;
    else if (imageData.startsWith('data:image')) fullUrl = imageData;
    else if (imageData.startsWith('/images/'))
      fullUrl = `${config.SITE_BASE_URL}${imageData}`;
    else if (imageData.startsWith('/'))
      fullUrl = `${config.SITE_BASE_URL}${imageData}`;
    else fullUrl = imageData;
    return { thumbnail: fullUrl, medium: fullUrl, large: fullUrl };
  }
  return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
};

const OrderSummary = ({
  deliveryFee = 0,
  distance = 0,
  couponDiscount = 0,
  couponCode = '',
}) => {
  const { cartItems, branchCarts, selectedBranch } = useCart();
  const { selectedCurrency, getCurrencySymbol } = useCurrency();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const currencySymbol = getCurrencySymbol(selectedCurrency);
  const decimals = getCurrencyDecimals(selectedCurrency);
  const convertedDeliveryFee = convertDeliveryFee(deliveryFee, selectedCurrency);

  const isCpActive = (item) =>
    item?.CPAccountActive === true ||
    item?.CPAccountActive === 1 ||
    item?.CPAccountActive === 'true' ||
    item?.CPAccountActive === '1';

  const formatPrice = (amount) => {
    const num = Number(amount);
    if (isNaN(num)) return '0';
    return num.toFixed(decimals);
  };

  // ✅ Build display groups: branchCarts first, fallback to flat cartItems.
  const displayGroups = React.useMemo(() => {
    if (branchCarts && branchCarts.length > 0) return branchCarts;
    if (cartItems && cartItems.length > 0) {
      return [
        {
          branch: selectedBranch || { Id: null, Name: 'Cart' },
          items: cartItems,
        },
      ];
    }
    return [];
  }, [branchCarts, cartItems, selectedBranch]);

  // Subtotal across ALL branch groups (MIRRORS MOBILE 1:1)
  // CP active (=1)            → main price EXCLUDED (subs only)
  // CP inactive (0/null/etc.) → main price COUNTED (parent + subs)
  const subtotal = displayGroups.reduce((groupSum, group) => {
    return (
      groupSum +
      group.items.reduce((sum, item) => {
        if (
          item.isCustom &&
          Array.isArray(item.SubProducts) &&
          item.SubProducts.length > 0
        ) {
          let subTotal = 0;
          item.SubProducts.forEach((sp) => {
            const subPrice = getPriceInCurrency(sp, selectedCurrency);
            const subQty = Number(sp.Quantity || 1);
            subTotal += subPrice * subQty;
          });
          // ✅ INVERTED RULE
          const mainPrice = isCpActive(item)
            ? 0
            : getPriceInCurrency(item, selectedCurrency);
          return sum + (mainPrice + subTotal) * Number(item.quantity);
        }
        if (
          item.isCustom &&
          item.CustomTotal !== undefined &&
          item.CustomTotal !== null
        )
          return sum + Number(item.CustomTotal) * Number(item.quantity);
        const price = getPriceInCurrency(item, selectedCurrency);
        return sum + price * Number(item.quantity);
      }, 0)
    );
  }, 0);

  const total = subtotal + convertedDeliveryFee - couponDiscount;

  // ✅ NEW: 1000 YER minimum (converted to active currency)
  const minOrderInCurrency = convertDeliveryFee(MIN_ORDER_YER, selectedCurrency);
  const isBelowMinimum = subtotal > 0 && subtotal < minOrderInCurrency;

  const renderItem = (item, idx, branchId) => {
    const rawImageData =
      item.ImageUrl || item.image || item.img || item.photo;
    const imageUrls = getImageUrls(rawImageData);
    const displayUrl = imageUrls.thumbnail;
    const isCustom =
      item.isCustom &&
      Array.isArray(item.SubProducts) &&
      item.SubProducts.length > 0;

    // ✅ INVERTED CP RULE (matches mobile)
    let singlePrice = 0;
    if (isCustom && item.CustomTotal) {
      singlePrice = Number(item.CustomTotal);
    } else if (isCustom) {
      let subTotal = 0;
      item.SubProducts.forEach((sp) => {
        const subPrice = getPriceInCurrency(sp, selectedCurrency);
        const subQty = Number(sp.Quantity || 1);
        subTotal += subPrice * subQty;
      });
      // CP active → exclude main; CP inactive → include main
      singlePrice =
        (isCpActive(item) ? 0 : getPriceInCurrency(item, selectedCurrency)) +
        subTotal;
    } else {
      singlePrice = getPriceInCurrency(item, selectedCurrency);
    }
    const itemTotal = singlePrice * Number(item.quantity);
    const itemKey = `${item.Id || item.ProductId || idx}_${branchId ?? ''}_${
      item.SelectedColor || ''
    }_${item.SelectedSize || ''}`;

    return (
      <Box key={itemKey}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: { xs: 2, sm: 2.5, md: 3 },
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Avatar
            src={displayUrl}
            variant="rounded"
            sx={{
              width: { xs: '100%', sm: 70, md: 90 },
              height: { xs: 120, sm: 70, md: 90 },
              borderRadius: { xs: 2, sm: 2.5, md: 3 },
              bgcolor: 'action.hover',
              boxShadow: 1,
            }}
            imgProps={{
              onError: (e) => {
                e.target.src =
                  'https://kaybees.com.pk/cdn/shop/products/ChickenBurger_1_1512x.jpg?v=1686040732';
              },
            }}
          />
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minHeight: { xs: 'auto', sm: 70, md: 90 },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: { xs: 1, sm: 1 },
                gap: 1,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  fontWeight="700"
                  sx={{
                    lineHeight: 1.2,
                    maxWidth: { xs: '100%', sm: '70%' },
                    fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' },
                  }}
                >
                  {item.ProductName || item.name}
                </Typography>

                {item.SelectedColor && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.8,
                      mt: 0.8,
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 14, sm: 16 },
                        height: { xs: 14, sm: 16 },
                        borderRadius: '50%',
                        bgcolor: 'secondary.main',
                        border: '2px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'secondary.main',
                        fontWeight: 700,
                        fontSize: { xs: '0.8rem', sm: '0.9rem', md: '0.95rem' },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <span style={{ fontSize: '1.1em' }}>🎨</span>{' '}
                      {item.SelectedColor}
                    </Typography>
                  </Box>
                )}

                {item.SelectedSize && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.8,
                      mt: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 14, sm: 16 },
                        height: { xs: 14, sm: 16 },
                        borderRadius: 1,
                        bgcolor: '#ed6c02',
                        border: '2px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          color: 'white',
                          fontSize: '0.6rem',
                          fontWeight: 800,
                        }}
                      >
                        S
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#ed6c02',
                        fontWeight: 700,
                        fontSize: { xs: '0.8rem', sm: '0.9rem', md: '0.95rem' },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <span style={{ fontSize: '1.1em' }}>📏</span>{' '}
                      {item.SelectedSize}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography
                variant="h6"
                fontWeight="800"
                color="primary.main"
                sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}
              >
                {formatPrice(itemTotal)} {currencySymbol}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Typography
                variant="body1"
                color="text.secondary"
                fontWeight="500"
                sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' } }}
              >
                {formatPrice(singlePrice)} {currencySymbol}
              </Typography>
              <Chip
                label={`${t('checkout.qty', 'Qty').replace(':', '')}: ${
                  item.quantity
                }`}
                color="default"
                sx={{
                  fontWeight: 700,
                  borderRadius: { xs: 1.5, sm: 2 },
                  height: { xs: 24, sm: 26, md: 28 },
                  fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' },
                }}
              />
            </Box>
          </Box>
        </Box>

        {isCustom && (
          <Box
            sx={{
              mt: { xs: 2, sm: 2 },
              ml: { xs: 0, sm: 2 },
              pl: { xs: 2, sm: 2 },
              borderLeft: '3px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 'bold',
                color: 'primary.main',
                mb: 1,
                fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' },
              }}
            >
              {t('customProduct.selectedSubproducts', 'Selected Options')}:
            </Typography>
            <Stack spacing={{ xs: 0.8, sm: 1 }}>
            {item.SubProducts.map((sp, spIdx) => {
              const spName = sp.ProductName || sp.Name;
              if (!spName) return null;

              const spPrice = getPriceInCurrency(sp, selectedCurrency);

              const hasQty =
                sp.Quantity !== undefined &&
                sp.Quantity !== null &&
                sp.Quantity !== '' &&
                Number(sp.Quantity) !== 0;

              return (
                <Typography
                  key={sp.Id || sp.id || spIdx}
                  variant="body2"
                  component="div"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.9rem' },
                    color: 'text.secondary',
                    lineHeight: 1.6,
                  }}
                >
                  <span style={{ fontWeight: 700, color: theme.palette.text.primary }}>
                    • {spName}
                  </span>
                  {sp.Description && (
                    <>
                      {' - '}
                      <span>{sp.Description}</span>
                    </>
                  )}
                  {spPrice > 0 && (
                    <>
                      {' - '}
                      <span style={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                        {t('price', 'Price')}: {formatPrice(spPrice)} {currencySymbol}
                      </span>
                    </>
                  )}
                  {hasQty && (
                    <span style={{ color: theme.palette.error.main, fontWeight: 600 }}>
                      {' - '}
                      {t('quantity', 'Qty')}: {sp.Quantity}
                    </span>
                  )}
                  {sp.SizeType && (
                    <>
                      {' - '}
                      <span>
                        {t('sizeType', 'Size Type')}: {sp.SizeType}
                      </span>
                    </>
                  )}
                  {sp.Height &&
                    sp.Height !== 'null' &&
                    Number(sp.Height) !== 0 && (
                      <>
                        {' - '}
                        <span>
                          {t('height', 'Height')}: {sp.Height}
                        </span>
                      </>
                    )}
                  {sp.Width &&
                    sp.Width !== 'null' &&
                    Number(sp.Width) !== 0 && (
                      <>
                        {' - '}
                        <span>
                          {t('width', 'Width')}: {sp.Width}
                        </span>
                      </>
                    )}
                </Typography>
              );
            })}
            </Stack>
          </Box>
        )}
      </Box>
    );
  };

  const totalItemCount = displayGroups.reduce(
    (acc, g) => acc + g.items.length,
    0
  );

  return (
    <Card
      elevation={4}
      sx={{
        borderRadius: { xs: 3, sm: 4 },
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        direction: i18n.dir(),
      }}
    >
      <Box
        sx={{
          bgcolor: 'primary.main',
          p: { xs: 2, sm: 2.5, md: 3 },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1.5, sm: 2 },
        }}
      >
        <ShoppingBagIcon
          sx={{
            color: 'primary.contrastText',
            fontSize: { xs: 26, sm: 28, md: 32 },
          }}
        />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            color: 'primary.contrastText',
            letterSpacing: { xs: 0.5, sm: 1 },
            fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
          }}
        >
          {t('checkout.orderSummary')}
        </Typography>
      </Box>

      <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        {totalItemCount === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            {t('cart.empty', 'Your cart is empty')}
          </Typography>
        ) : (
          /* ✅ NEW: render each branch as its own section (matches mobile/Cart UX) */
          <Stack spacing={{ xs: 3, sm: 3.5, md: 4 }}>
            {displayGroups.map((group) => {
              const branch = group.branch;
              const branchId = getBranchId(branch);
              const branchName = getBranchName(branch);
              const branchAddress = getBranchAddress(branch);
              const isActiveBranch =
                selectedBranch && getBranchId(selectedBranch) === branchId;

              return (
                <Box
                  key={`branch-${branchId ?? 'global'}`}
                  sx={{
                    border: '1px solid',
                    borderColor: isActiveBranch ? 'primary.main' : 'divider',
                    borderRadius: { xs: 2, sm: 2.5 },
                    overflow: 'hidden',
                    bgcolor: 'background.default',
                  }}
                >
                  {/* Branch header */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: { xs: 1.5, sm: 2 },
                      py: { xs: 1, sm: 1.2 },
                      bgcolor: isActiveBranch
                        ? 'primary.light'
                        : 'action.hover',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <StorefrontIcon
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: isActiveBranch
                            ? 'primary.dark'
                            : 'text.secondary',
                        }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: '0.85rem', sm: '0.95rem' },
                            color: isActiveBranch
                              ? 'primary.dark'
                              : 'text.primary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {branchName}
                        </Typography>
                        {branchAddress && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.3,
                              mt: 0.2,
                            }}
                          >
                            <LocationOnIcon
                              sx={{ fontSize: 12, color: 'text.secondary' }}
                            />
                            <Typography
                              sx={{
                                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                color: 'text.secondary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {branchAddress}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Chip
                      size="small"
                      label={`${group.items.length} ${t(
                        'cart.items',
                        'items'
                      )}`}
                      sx={{
                        height: 22,
                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                        fontWeight: 700,
                        bgcolor: isActiveBranch
                          ? 'primary.main'
                          : 'background.paper',
                        color: isActiveBranch ? 'white' : 'text.primary',
                      }}
                    />
                  </Box>

                  {/* Branch items */}
                  <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Stack spacing={{ xs: 2.5, sm: 3 }}>
                      {group.items.map((item, idx) => (
                        <React.Fragment key={`${branchId}-${idx}`}>
                          {renderItem(item, idx, branchId)}
                          {idx < group.items.length - 1 && (
                            <Divider sx={{ borderStyle: 'dashed' }} />
                          )}
                        </React.Fragment>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}

        <Divider
          sx={{
            my: { xs: 3, sm: 3.5, md: 4 },
            borderStyle: 'dashed',
            borderWidth: 2,
          }}
        />

        <Stack spacing={{ xs: 2, sm: 2.2, md: 2.5 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h6"
              color="text.secondary"
              fontWeight="500"
              sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' } }}
            >
              {t('checkout.subtotal')}
            </Typography>
            <Typography
              variant="h6"
              fontWeight="700"
              sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' } }}
            >
              {formatPrice(subtotal)} {currencySymbol}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, sm: 1.5 },
                flexWrap: 'wrap',
              }}
            >
              <Typography
                variant="h6"
                color="text.secondary"
                fontWeight="500"
                sx={{
                  fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' },
                }}
              >
                {t('checkout.deliveryFee')}
              </Typography>
              {distance > 0 && (
                <Chip
                  label={`${distance.toFixed(1)} km`}
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{
                    fontWeight: 600,
                    height: { xs: 22, sm: 24, md: 28 },
                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                  }}
                />
              )}
            </Box>
            <Typography
              variant="h6"
              fontWeight="700"
              sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' } }}
            >
              {formatPrice(convertedDeliveryFee)} {currencySymbol}
            </Typography>
          </Box>

          {couponDiscount > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1, sm: 1.5 },
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  variant="h6"
                  color="success.main"
                  fontWeight="500"
                  sx={{
                    fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' },
                  }}
                >
                  {t('checkout.couponDiscount', 'Coupon Discount')}
                </Typography>
                {couponCode && (
                  <Chip
                    icon={<LocalOfferIcon />}
                    label={couponCode}
                    color="success"
                    variant="outlined"
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontFamily: 'monospace',
                      height: { xs: 22, sm: 24, md: 28 },
                      fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                    }}
                  />
                )}
              </Box>
              <Typography
                variant="h6"
                fontWeight="700"
                color="success.main"
                sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' } }}
              >
                -{formatPrice(couponDiscount)} {currencySymbol}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: { xs: 0.5, sm: 1 } }} />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: { xs: 1, sm: 1 },
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography
              variant="h5"
              fontWeight="900"
              color="text.primary"
              sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' } }}
            >
              {t('checkout.total')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography
                variant="h4"
                fontWeight="900"
                color="primary.main"
                sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem', md: '2rem' } }}
              >
                {formatPrice(Math.max(0, total))}
              </Typography>
              <Typography
                component="span"
                variant="h6"
                fontWeight="700"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' } }}
              >
                {currencySymbol}
              </Typography>
            </Box>
          </Box>

          {/* ✅ NEW: minimum order warning (matches mobile) */}
          {isBelowMinimum && (
            <Alert
              severity="error"
              icon={<WarningAmberIcon fontSize="inherit" />}
              sx={{
                borderRadius: { xs: 1.5, sm: 2 },
                fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' },
                fontWeight: 600,
                alignItems: 'center',
              }}
            >
              {t(
                'checkout.minOrderWarning',
                `Minimum order is ${formatPrice(
                  minOrderInCurrency
                )} ${currencySymbol}`
              )}
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default OrderSummary;